import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, FileWarning, Clock3, Truck, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([api.get("/admin/stats"), api.get("/admin/clients")]);
      setStats(s.data); setClients(c.data.slice(0, 6));
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="hs-overline text-blue-700">ADMIN CONSOLE</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-2">Operations</h1>
        <p className="text-sm text-slate-500 mt-2">Fleet-wide compliance status across all clients.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total Clients" value={stats?.total_clients ?? "—"} testid="stat-clients" />
        <Stat icon={Clock3} label="Pending Documents" value={stats?.pending_documents ?? "—"} testid="stat-pending-docs" accent="#D97706" />
        <Stat icon={FileWarning} label="Expiring 30d" value={stats?.expiring_filings_30d ?? "—"} testid="stat-expiring" accent="#DC2626" />
        <Stat icon={Truck} label="Pending Filings" value={stats?.pending_filings ?? "—"} testid="stat-pending-filings" />
      </div>

      <div className="hs-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="hs-overline">RECENT CLIENTS</div>
            <h3 className="font-display font-bold text-xl mt-1">Latest signups</h3>
          </div>
          <Link to="/admin/clients" className="hs-btn-secondary inline-flex items-center gap-2" data-testid="admin-view-all-clients">
            VIEW ALL <ArrowRight size={14} />
          </Link>
        </div>
        <div className="divide-y divide-[#1a1a1a]">
          {clients.length === 0 && <div className="p-6 text-sm text-slate-500">No clients yet.</div>}
          {clients.map((c) => (
            <Link to={`/admin/clients/${c.id}`} key={c.id} className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-slate-50" data-testid={`recent-client-${c.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-slate-500 truncate">{c.company_name || c.email}</div>
              </div>
              <div className="hidden sm:flex items-center gap-3 text-xs font-mono text-slate-500">
                {c.dot_number && <span>DOT {c.dot_number}</span>}
                {c.pending_documents > 0 && <span className="hs-tag hs-tag-warn">{c.pending_documents} PENDING</span>}
              </div>
              <ArrowRight size={14} className="text-slate-500" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, accent = "#1D4ED8" }) {
  return (
    <div className="hs-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="hs-overline">{label}</span>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="font-mono font-black text-3xl mt-3" style={{ color: accent }}>{value}</div>
    </div>
  );
}
